import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '../api-base-url.token';

export interface Period {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  comment?: string;
}

export interface ScanContext {
  id: string;
  name: string;
  description?: string;
  periods?: Period[];
}

export interface PeriodStats {
  totalScans: number;
  avgScansPerDay: {
    monday: { avg: number; count: number };
    tuesday: { avg: number; count: number };
    wednesday: { avg: number; count: number };
    thursday: { avg: number; count: number };
    friday: { avg: number; count: number };
    saturday: { avg: number; count: number };
    sunday: { avg: number; count: number };
  };
}

export interface PeriodStatsResponse {
  period: any;
  daily: {
    day_of_week: number;
    avg_scans: number;
    total_scans: number;
    occurrence_count: number;
  }[];
  timeslots: any[];
}

export interface PeriodAvg {
  periodId: string;
  avgScans: number;
}

export interface ScanStatsPoint {
  time: string;
  avgScans: number;
  periodAvgs: PeriodAvg[];
}

export interface ScanStatsResponse {
  queueId: string;
  contextId: string;
  contextName: string;
  dayOfWeek: number;
  periods: { id: string; name: string; startDate: string; endDate: string }[];
  points: ScanStatsPoint[];
}

@Injectable({ providedIn: 'root' })
export class ScanContextService {
  private http = inject(HttpClient);
  private base = inject(API_BASE_URL);

  private mapContext(raw: any): ScanContext {
    return {
      id: raw.id,
      name: raw.name,
      description: raw.description,
      periods: raw.periods?.map((p: any) => this.mapPeriod(p))
    };
  }

  private mapPeriod(raw: any): Period {
    return {
      id: raw.id,
      name: raw.name,
      startDate: raw.start_date || raw.startDate,
      endDate: raw.end_date || raw.endDate,
      comment: raw.comment
    };
  }

  // Contextes
  getContexts(queueId: string): Observable<ScanContext[]> {
    return this.http.get<any[]>(`${this.base}/api/v1/queues/${queueId}/scan-contexts`)
      .pipe(map(contexts => contexts.map(c => this.mapContext(c))));
  }

  createContext(queueId: string, data: { name: string; description?: string; period?: Omit<Period, 'id'> }): Observable<ScanContext> {
    const payload: any = {
      name: data.name,
      description: data.description
    };
    
    if (data.period) {
      payload.period = {
        name: data.period.name,
        startDate: data.period.startDate,
        endDate: data.period.endDate,
        comment: data.period.comment || null
      };
    }
    
    return this.http.post<any>(`${this.base}/api/v1/queues/${queueId}/scan-contexts`, payload)
      .pipe(map(c => this.mapContext(c)));
  }

  updateContext(queueId: string, contextId: string, data: { name: string; description?: string }): Observable<ScanContext> {
    return this.http.put<any>(`${this.base}/api/v1/queues/${queueId}/scan-contexts/${contextId}`, data)
      .pipe(map(c => this.mapContext(c)));
  }

  deleteContext(queueId: string, contextId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/api/v1/queues/${queueId}/scan-contexts/${contextId}`);
  }

  // Périodes
  getPeriods(queueId: string, contextId: string): Observable<Period[]> {
    return this.http.get<any[]>(`${this.base}/api/v1/queues/${queueId}/scan-contexts/${contextId}/periods`)
      .pipe(map(periods => periods.map(p => this.mapPeriod(p))));
  }

  createPeriod(queueId: string, contextId: string, data: Omit<Period, 'id'>): Observable<Period> {
    const payload = {
      name: data.name,
      startDate: data.startDate,
      endDate: data.endDate,
      comment: data.comment || null
    };
    return this.http.post<any>(`${this.base}/api/v1/queues/${queueId}/scan-contexts/${contextId}/periods`, payload)
      .pipe(map(p => this.mapPeriod(p)));
  }

  updatePeriod(queueId: string, contextId: string, periodId: string, data: Omit<Period, 'id'>): Observable<Period> {
    const payload = {
      name: data.name,
      startDate: data.startDate,
      endDate: data.endDate,
      comment: data.comment
    };
    return this.http.put<any>(`${this.base}/api/v1/queues/${queueId}/scan-contexts/${contextId}/periods/${periodId}`, payload)
      .pipe(map(p => this.mapPeriod(p)));
  }

  deletePeriod(queueId: string, contextId: string, periodId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/api/v1/queues/${queueId}/scan-contexts/${contextId}/periods/${periodId}`);
  }

  // Stats
  getPeriodStats(queueId: string, contextId: string, periodId: string): Observable<PeriodStats> {
    return this.http.get<PeriodStatsResponse>(`${this.base}/api/v1/queues/${queueId}/scan-contexts/${contextId}/periods/${periodId}/stats`)
      .pipe(map(response => {
        const avgScansPerDay = {
          monday: { avg: 0, count: 0 },
          tuesday: { avg: 0, count: 0 },
          wednesday: { avg: 0, count: 0 },
          thursday: { avg: 0, count: 0 },
          friday: { avg: 0, count: 0 },
          saturday: { avg: 0, count: 0 },
          sunday: { avg: 0, count: 0 }
        };

        response.daily.forEach(day => {
          const avgScans = Math.round(day.avg_scans);
          const count = day.occurrence_count;
          switch (day.day_of_week) {
            case 1: avgScansPerDay.monday = { avg: avgScans, count }; break;
            case 2: avgScansPerDay.tuesday = { avg: avgScans, count }; break;
            case 3: avgScansPerDay.wednesday = { avg: avgScans, count }; break;
            case 4: avgScansPerDay.thursday = { avg: avgScans, count }; break;
            case 5: avgScansPerDay.friday = { avg: avgScans, count }; break;
            case 6: avgScansPerDay.saturday = { avg: avgScans, count }; break;
            case 7: avgScansPerDay.sunday = { avg: avgScans, count }; break;
          }
        });

        return {
          totalScans: response.period.total_scans || 0,
          avgScansPerDay
        };
      }));
  }

  // Stats par créneau (pour le tooltip du graphe)
  getScanStats(queueId: string, contextId: string, dayOfWeek: number): Observable<ScanStatsResponse> {
    return this.http.get<any>(
      `${this.base}/api/v1/queues/${queueId}/scan-contexts/${contextId}/scan-stats`,
      { params: { dayOfWeek: String(dayOfWeek) } }
    ).pipe(map(raw => ({
      queueId: raw.queueId || raw.queue_id,
      contextId: raw.contextId || raw.context_id,
      contextName: raw.contextName || raw.context_name,
      dayOfWeek: raw.dayOfWeek || raw.day_of_week,
      periods: (raw.periods || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        startDate: p.startDate || p.start_date,
        endDate: p.endDate || p.end_date
      })),
      points: (raw.points || []).map((pt: any) => ({
        time: pt.time,
        avgScans: pt.avgScans || pt.avg_scans || 0,
        periodAvgs: (pt.periodAvgs || pt.period_avgs || []).map((pa: any) => ({
          periodId: pa.periodId || pa.period_id,
          avgScans: pa.avgScans || pa.avg_scans || 0
        }))
      }))
    })));
  }
}
